/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  HOC for loading data before rendering a component
 */

import { useMountAndUnmount } from '@zcode-ui/hook'
import { selectIsBot, selectIsMounted } from '@zcode-ui/web-zumper-global-state'
import { withRouter } from '@zcode/router-utils'
import { noop } from '@zcode/utils'
import hoistStatics from 'hoist-non-react-statics'
import PropTypes from 'prop-types'
import React, { createContext, useContext, useRef, useState } from 'react'
import { useStore } from 'react-redux'
import invariant from 'tiny-invariant'
import { logger } from '../../../logger'
import { ON_BROWSER } from '../../../shared/env'
import isLocationDifferent from './isLocationDifferent'
import { createExpressReq, createLegacyProps } from './legacy'
import { useScrollXError } from './useScrollXError'
const defaultDelay = 200
const defaultTimeout = 1000

const LoadDataContext = createContext(Promise.resolve())

// Options are modelled after react-loadable
// https://github.com/jamiebuilds/react-loadable#loadable-and-loadablemap-options
/**
 * @type {import('./withLoadDataType').WithLoadData}
 */
export const withLoadData = (
  _loadData,
  {
    delay = defaultDelay,
    timeout = defaultTimeout,
    legacy = false,
    loadInParallel = false,

    // TODO: react-loadable requires loading because of how it works, but
    // withLoadData doesn't necessarily require the data to be loaded before
    // rendering the wrapped component. We need to come up with a rational
    // default for when to show a loading component. In practice, we never
    // needed a loading component on NPR.
    //
    // On both NPR and NZR, we always pass a null loading component to
    // react-loadable. Basically, we don't typically utilize these loading
    // components.
    loading = undefined,

    // TODO: these defaults need tweaked
    showLoadingOnlyPastDelay = false,
    ignoreLocationChange = false,
    ignoreHashChange = false,
    ignoreSearchChange = false,

    // NOTE: This will cause the loadData function to be passed to the wrapped component as a prop.
    // From the wrapped component, calling this.props.loadData() will trigger a load data call with
    // all the correct arguments.
    passAsProp = false,
    shouldLoadDataUpdate,
    skipForBotsOnBrowser = false,
  } = {}
) => {
  invariant(typeof _loadData === 'function', 'loadData must be a function')
  return (WrappedComponent) => {
    const WithLoadData = (props) => {
      const { history, location, match } = props
      const store = useStore()
      const loadDataPromise = useContext(LoadDataContext)
      const promiseRef = useRef(loadDataPromise)
      const { getState } = store
      const state = getState()
      const appMounted = selectIsMounted(state)
      const isBot = selectIsBot(state)
      const loadData = skipForBotsOnBrowser && isBot && ON_BROWSER ? noop : _loadData
      const [error, setError] = useState(false)
      const [loaded, setLoaded] = useState(!appMounted)
      const [pastDelay, setPastDelay] = useState(false)
      const [timedOut, setTimedOut] = useState(false)

      useScrollXError(loaded, WrappedComponent?.displayName)

      const prevLocationRef = useRef(location)
      const prevLocation = prevLocationRef.current
      prevLocationRef.current = location

      const timeoutRef = useRef()
      const deferRef = useRef()
      const unmountedRef = useRef(false)

      const clearTimeouts = () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          if (!unmountedRef.current) {
            setTimedOut(false)
          }
          timeoutRef.current = undefined
        }
        if (deferRef.current) {
          clearTimeout(deferRef.current)
          if (!unmountedRef.current) {
            setPastDelay(false)
          }
          deferRef.current = undefined
        }
      }

      const loadDataCallback = async () => {
        const routeProps = { history, location, match }
        if (legacy) {
          const legacyProps = createLegacyProps(store, routeProps)
          const expressReq = createExpressReq(store, routeProps)
          return loadData(legacyProps, expressReq)
        }
        return loadData(store, routeProps)
      }

      const beginLoadData = () => {
        // clear state
        if (!unmountedRef.current) {
          setError(false)
          setLoaded(false)
          setPastDelay(false)
          setTimedOut(false)
        }

        // configure timeouts
        clearTimeouts()
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = undefined
          if (!unmountedRef.current) {
            setTimedOut(true)
          }
        }, timeout)

        deferRef.current = setTimeout(() => {
          deferRef.current = undefined
          if (!unmountedRef.current) {
            setPastDelay(true)
          }
        }, delay)

        // load the data
        const parentPromise = loadInParallel ? Promise.resolve() : loadDataPromise
        const promise = parentPromise
          .then(loadDataCallback)
          .then(() => {
            clearTimeouts()
            if (!unmountedRef.current) {
              setLoaded(true)
            }
          })
          .catch((error) => {
            logger.warn('loadData failed', error)
            clearTimeouts()
            if (!unmountedRef.current) {
              setError(true)
            }
          })

        if (loadInParallel) {
          promiseRef.current = Promise.all([promise, loadDataPromise])
        } else {
          promiseRef.current = promise
        }
      }

      if (appMounted && !ignoreLocationChange) {
        // NOTE: We should only re-load data after the browser location has changed. We presume
        // that data is preloaded on the server. We also presume that fetching new data should
        // only happen for client-side route changes.
        //
        // Here we rely on strict equality of the react-router location to determine route
        // changes. This would likely happen if the url changed but it matched the same route.
        //
        // React-router garuntees that the location is immutable.
        // https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/api/location.md

        const locationChanged = isLocationDifferent(location, prevLocation, {
          ignoreHashChange,
          ignoreSearchChange,
        })
        let shouldUpdate = false
        try {
          shouldUpdate =
            typeof shouldLoadDataUpdate === 'function' && locationChanged
              ? shouldLoadDataUpdate(location, prevLocation)
              : locationChanged
        } catch (e) {
          logger.error('error while checking shouldUpdate from withLoadData', e)
        }

        if (shouldUpdate) {
          beginLoadData()
        }
      }

      useMountAndUnmount({
        onMount: () => {
          if (typeof WrappedComponent.preload === 'function') {
            WrappedComponent.preload()
          }
          unmountedRef.current = false
          if (appMounted) {
            beginLoadData()
          }
        },
        onUnmount: () => {
          unmountedRef.current = true
        },
      })

      const showLoading = showLoadingOnlyPastDelay ? pastDelay : true
      const Loading = loading

      // Never show Loading on server or during hydration
      if (ON_BROWSER && Loading && showLoading && !loaded) {
        return <Loading {...props} error={error} pastDelay={pastDelay} timedOut={timedOut} />
      }
      const { wrappedComponentRef, ...remainingProps } = props

      if (passAsProp) {
        remainingProps.loadData = loadDataCallback
      }

      // expose loadDataErrored, loading, pastDelay and timedOut to the WrappedComponent
      remainingProps.loadDataErrored = error
      remainingProps.loading = !loaded
      remainingProps.pastDelay = !loaded && !!pastDelay
      remainingProps.timedOut = !loaded && !!timedOut

      return (
        <LoadDataContext.Provider value={promiseRef.current}>
          <WrappedComponent {...remainingProps} ref={wrappedComponentRef} />
        </LoadDataContext.Provider>
      )
    }

    WithLoadData.propTypes = {
      history: PropTypes.object,
      location: PropTypes.object,
      match: PropTypes.object,
      wrappedComponentRef: PropTypes.func,
    }
    WithLoadData.displayName = `withLoadData(${
      WrappedComponent.displayName || WrappedComponent.name
    })`
    WithLoadData.WrappedComponent = WrappedComponent

    WithLoadData.loadData = !legacy ? _loadData : undefined
    WithLoadData.satisfy = legacy ? _loadData : undefined

    hoistStatics(WithLoadData, WrappedComponent)
    return withRouter(WithLoadData)
  }
}
